import {
  AfterViewInit,
  ChangeDetectorRef,
  Component, DestroyRef, inject, input, OnInit,
  output,
  Signal,
  signal,
} from '@angular/core';
import {
  FormArray, FormBuilder, FormGroup, Validators,
} from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { catchError, lastValueFrom, Observable, tap, throwError } from 'rxjs';
import { AdminService } from '../../../../admin/services/admin.service';
import { displayProduct } from '../../../../models/product.model';
import { Warehouse } from '../../../../models/warehouse.model';
import { InventoryActionService } from '../../../../services/inventory-action.service';
import { ProductsService } from '../../../../services/products.service';
import { WarehousesService } from '../../../../services/warehouses.service';
import { toUnderscore } from '../../../../utils/string-utils';
import { Category } from '../../../../models/category.model';
import { CategoriesService } from '../../../../services/categories.service';
import { generateInventoryActionFormGroup, InventoryAction } from '../../../../models/inventory_action.model';
import { Provider } from '../../../../models/provider.model';
import { ProviderService } from '../../../../providers/provider.service';
import { ProviderFormComponent } from '../../../../providers/provider-form/provider-form.component';
import { UserService } from '../../../../services/user.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FoldersService } from '../../../../services/folders.service';
import { Folder } from '../../../../models/folder.model';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';


@Component({
    selector: 'app-inventory-action-create-form',
    template: '',
    styleUrl: './inventory-action-create-form.component.scss',
})
export class InventoryActionCreateFormComponent  implements OnInit, AfterViewInit {
  isPluginMode = input(false);
  
  workInstanceId = input<number | undefined>();

  updated = output();

  categories: Category[];

  public inventoriableCategoriesIds: number[];

  protected readonly fb = inject(FormBuilder);

  form: FormGroup = this.fb.group({
    owner_id: [null],
    owner_type: [null],
    estimated_completion_date: [null],
    inventory_action_products: this.fb.array([]),
    warehouse_id: [null, [Validators.required]],
    warehouse_id_sender: [null],
  });

  protected loading = signal(false);

  inventoryActionType: string;

  title: string;

  providers: Signal<Provider[] | undefined>;

  displayProduct = displayProduct;
  
  warehouses$: Observable<Warehouse[]>;


  protected readonly adminService = inject(AdminService);
  
  private readonly categoryService = inject(CategoriesService)
  
  protected readonly cdr = inject(ChangeDetectorRef);
  
  public readonly data = inject(MAT_DIALOG_DATA);

  protected readonly destroyRef = inject(DestroyRef);

  protected readonly folderService = inject(FoldersService);
  
  protected readonly inventoryActionService = inject(InventoryActionService);
  
  private readonly matDialog = inject(MatDialog);
  
  private readonly matDialogRef = inject(MatDialogRef<InventoryActionCreateFormComponent>);
  
  public readonly productsService = inject(ProductsService);

  protected readonly providerService = inject(ProviderService);

  protected readonly snackBar = inject(MatSnackBar);

  protected readonly userService = inject(UserService);

  protected readonly warehousesService = inject(WarehousesService);

  constructor() {}

  ngOnInit(): void {
    if (this.data.edit) {
      this.inventoryActionType = this.data.inventoryAction.type;
      this.form = generateInventoryActionFormGroup(this.data.inventoryAction);
    } else {
      this.inventoryActionType = this.data.inventoryActionType;
    }

    this.categoryService.getInventoriableCategories().subscribe((categories) => {
      this.cdr.markForCheck();
      this.inventoriableCategoriesIds = categories.map((category) => category.id);
    });

    this.title = this.adminService.getText(toUnderscore(this.inventoryActionType));
    this.warehouses$ = this.warehousesService.index();
  }

  ngAfterViewInit(): void {
    this.setFormHandlers();
    this.setFormControlValidators();
  }

  protected setFormHandlers() {
    this.form.valueChanges.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(() => {
      this.updated.emit();
    });
  }

  protected setFormControlValidators() {}

  addProvider() {
    this.matDialog.open(ProviderFormComponent, {
      width: '500px',
    });
  }

  protected getFormDataForSending() {
    const formData = this.form.getRawValue();

    formData.inventory_action_products = this.inventoryActionProducts.getRawValue();
    
    formData.inventory_action_products_attributes = formData.inventory_action_products.map(
      (product) => ({
        ...product,
        warehouse_id: this.form.get('warehouse_id').value,
      }),
    );

    delete formData['inventory_action_products'];

    formData.type = this.inventoryActionType;
    return formData;
  }

  protected getFormDataForMultipleSending(): Partial<Folder> {
    const formData = {
      documents_attributes: this.getDocumentsAttributes(),
      type: `Folder::${this.inventoryActionType}`,
    };

    if (this.workInstanceId()) {
      formData['work_instance_id'] = this.workInstanceId();
    }

    return formData;
  }

  protected getDocumentsAttributes(): Record<string, any>[] | never {
    throw new Error('Method not implemented.');
  }

  async saveForm() {
    this.loading.set(true);
    
    if (this.isMultiple()) {
      const formData = this.getFormDataForMultipleSending();
      await this.saveFormMultiple(formData);
    } else {
      const formData = this.getFormDataForSending();
      await this.saveFormSingle(formData);
    }
  }

  private saveFormSingle(formData: Record<string, any>): Promise<any> {
    let observable: Observable<InventoryAction>;

    if (formData.id) {
      observable = this.inventoryActionService.update(formData.id, formData)
    } else {
      observable = this.inventoryActionService.create({
        inventory_action: formData,
      });
    }

    return lastValueFrom(observable.pipe(
      catchError((e) => {
        this.loading.set(false);
        this.snackBar.open(`Error al crear ${this.typeHumanName}`, 'Cerrar', { duration: 5000 });
        return throwError(() => e);
      }),
      tap(() => {
        this.loading.set(false);
        if (!this.isPluginMode()) {
          this.matDialogRef.close(true);
        }
      })
    ));
  }

  private saveFormMultiple(formData: Record<string, any>): Promise<any> {
    return lastValueFrom(
      this.folderService
        .create({
          folder: formData,
        })
        .pipe(
          catchError((e) => {
            this.loading.set(false);
            this.snackBar.open(`Error al crear ${this.typeHumanName}`, 'Cerrar', { duration: 5000 });
            return throwError(() => e);;
          }),
          tap(() => {
            this.loading.set(false);
            if (!this.isPluginMode()) {
              this.matDialogRef.close(true);
            }
          }),
        )
    );
  }

  protected isMultiple() {
    return false;
  }

  editInventoryActionInstantly() {
    const formData = this.getFormDataForSending();

    this.inventoryActionService
      .update(this.data.inventoryAction.id, formData)
      .subscribe(() => {
        this.matDialogRef.close(true);
      });
  }

  get formValid() {
    return this.form.valid && this.inventoryActionProducts?.value?.length > 0;
  }

  get type() {
    return this.inventoryActionType.split('InventoryAction::')[1];
  }

  get typeHumanName() {
    return this.adminService.getText(this.inventoryActionType);
  }

  get inventoryActionProducts(): FormArray {
    return this.form.get('inventory_action_products') as FormArray;
  }

  set inventoryActionProducts(products: any) {
    this.form.controls.inventory_action_products = products;
  }
}
